import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Plus,
  Users,
  ArrowLeft,
  Activity,
  Clock,
  FileText,
  Shield,
} from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  fetchWorkspace,
  fetchWorkspaceBoards,
} from '../../store/workspaceSlice';
import { usePermissions } from '../../hooks/usePermissions';
import WorkspaceHeader from '../../components/Workspace/WorkspaceHeader';
import BoardList from '../../components/Workspace/BoardList';
import AddMemberModal from '../../components/Workspace/AddMemberModal';
import EditWorkspaceModal from '../../components/Workspace/EditWorkspaceModal';
import CreateBoardModal from '../../components/Workspace/CreateBoardModal';
import ActivityFeed from '../../components/Activity/ActivityFeed';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import TeamsPage from '../Teams/TeamsPage';
import './WorkspaceDashboard.scss';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const WorkspaceDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentWorkspace, workspaceBoards = [], loading = false } = useAppSelector(
    (state) => state.workspaces || {}
  );
  const { isOwnerOrAdmin } = usePermissions(currentWorkspace?.id);

  const [currentTab, setCurrentTab] = useState(0);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [editWorkspaceModalOpen, setEditWorkspaceModalOpen] = useState(false);
  const [createBoardModalOpen, setCreateBoardModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchWorkspace(id));
      dispatch(fetchWorkspaceBoards(id));
    }
    // Deliberately no unmount cleanup that clears currentWorkspace here -
    // it's app-wide shared state now (the header's WorkspaceSwitcher and
    // any board-agnostic route depend on it staying set to "whatever
    // workspace was last active", the same way currentBoard behaves).
    // Navigating to a different workspace's dashboard already overwrites
    // it via fetchWorkspace(id) above; nulling it out on every unmount
    // instead blanked it for any page reached afterwards.
  }, [id, dispatch]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleBack = () => {
    navigate('/workspaces');
  };

  const handleAddMember = () => {
    setAddMemberModalOpen(true);
  };

  const handleEditWorkspace = () => {
    setEditWorkspaceModalOpen(true);
  };

  if (loading && !currentWorkspace) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentWorkspace) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Workspace not found
        </Typography>
        <Button startIcon={<ArrowLeft />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Workspaces
        </Button>
      </Container>
    );
  }

  return (
    <Box className="workspace-dashboard">
      <WorkspaceHeader
        workspace={currentWorkspace}
        onBack={handleBack}
        onEdit={handleEditWorkspace}
        onAddMember={handleAddMember}
        isOwnerOrAdmin={isOwnerOrAdmin}
      />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Breadcrumbs
          items={[
            { label: 'Workspaces', path: '/workspaces' },
            { label: currentWorkspace.name },
          ]}
        />
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab
              icon={<Clock size={18} />}
              iconPosition="start"
              label="Recents"
            />
            <Tab
              icon={<FileText size={18} />}
              iconPosition="start"
              label="Content"
            />
            <Tab
              icon={<Shield size={18} />}
              iconPosition="start"
              label="Permissions"
            />
            <Tab icon={<Activity size={18} />} iconPosition="start" label="Activity" />
            <Tab icon={<Users size={18} />} iconPosition="start" label="Teams" />
          </Tabs>
        </Box>

        {/* Recents Tab */}
        <TabPanel value={currentTab} index={0}>
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Recently Accessed
            </Typography>
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                color: 'text.secondary',
              }}
            >
              <Clock size={48} strokeWidth={1} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                No Recent Items
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Your recently accessed boards, folders, and dashboards will appear here
              </Typography>
            </Box>
          </Box>
        </TabPanel>

        {/* Content Tab */}
        <TabPanel value={currentTab} index={1}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h6">Workspace Content</Typography>
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={() => setCreateBoardModalOpen(true)}
              >
                Create Board
              </Button>
            </Box>
            <BoardList
              workspaceId={currentWorkspace.id}
              boards={workspaceBoards}
              loading={loading}
              isOwnerOrAdmin={isOwnerOrAdmin}
            />
          </Box>
        </TabPanel>

        {/* Permissions Tab */}
        <TabPanel value={currentTab} index={2}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h6">Workspace Members</Typography>
              {isOwnerOrAdmin && (
                <Button
                  variant="outlined"
                  startIcon={<Plus size={18} />}
                  onClick={handleAddMember}
                >
                  Add Member
                </Button>
              )}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 2,
              }}
            >
              {(currentWorkspace.members || []).filter(Boolean).map((member) => {
                const displayName = member?.user?.name || member?.user?.userName || member?.user?.email || `User ${member?.userId}`;
                const displayEmail = member?.user?.email || '';
                const initials = displayName.charAt(0).toUpperCase();
                return (
                  <Box key={member.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              backgroundColor: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          >
                            {initials}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {displayName}
                            </Typography>
                            {displayEmail && (
                              <Typography variant="caption" color="text.secondary">
                                {displayEmail}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <Chip label={member.role} size="small" color="primary" />
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                );
              })}
            </Box>

            {(!currentWorkspace.members || currentWorkspace.members.length === 0) && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  color: 'text.secondary',
                }}
              >
                <Users size={48} strokeWidth={1} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  No members yet
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Add members to collaborate on this workspace
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Activity Tab */}
        <TabPanel value={currentTab} index={3}>
          {currentTab === 3 && <ActivityFeed workspaceId={currentWorkspace.id} />}
        </TabPanel>

        {/* Teams Tab */}
        <TabPanel value={currentTab} index={4}>
          {currentTab === 4 && <TeamsPage />}
        </TabPanel>
      </Container>

      {/* Modals */}
      <AddMemberModal
        open={addMemberModalOpen}
        onClose={() => setAddMemberModalOpen(false)}
        workspaceId={currentWorkspace.id}
      />
      <EditWorkspaceModal
        open={editWorkspaceModalOpen}
        onClose={() => setEditWorkspaceModalOpen(false)}
        workspace={currentWorkspace}
      />
      <CreateBoardModal
        open={createBoardModalOpen}
        onClose={() => setCreateBoardModalOpen(false)}
        workspaceId={currentWorkspace.id}
      />
    </Box>
  );
};

export default WorkspaceDashboard;
